/**
 * CSV export for the Firm Contacts directory.
 *
 * Table-shaped CSV matching the on-screen columns (Name, Category, Type,
 * contact details, jobs using). Triggered from `FirmContactsView` as
 * "Current view" (honours search/filters) or "All contacts".
 *
 * Numeric columns (Jobs) are written as real numbers so they sort / sum
 * in Excel. Operates on the in-memory rows already loaded for the table.
 */

import type { CustomExportDataset } from "@/components/customExport";
import {
  CONTACT_TYPE_META,
  FIRM_CATEGORY_META,
  firmContactState,
  formatAbn,
  formatAcn,
  type FirmContactRow,
} from "./types";

const TABLE_HEADERS = [
  "Name",
  "ACN",
  "ABN",
  "Category",
  "Type",
  "Email",
  "Phone",
  "State",
  "Address",
  "Jobs",
  "Status",
] as const;

const STATUS_LABEL: Record<FirmContactRow["status"], string> = {
  active: "Active",
  archived: "Archived",
  merged: "Merged",
  deleted: "Deleted",
};

export type FirmContactsCsvScope = "current" | "all";

function escapeCsvCell(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return "";
  const s = String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function displayName(contact: FirmContactRow): string {
  if (!contact.linkedUser) return contact.displayName;
  const fromName = [contact.linkedUser.firstName, contact.linkedUser.lastName]
    .filter((s): s is string => Boolean(s))
    .join(" ");
  return fromName || contact.linkedUser.email || contact.displayName;
}

function formatAddress(contact: FirmContactRow): string {
  if (contact.addresses.length === 0) return "";
  return contact.addresses
    .map((address) =>
      [
        address.line1,
        address.line2,
        address.suburb,
        address.state,
        address.postcode,
      ]
        .filter(Boolean)
        .join(", "),
    )
    .filter(Boolean)
    .join(" | ");
}

function rowToTableRecord(contact: FirmContactRow): Array<string | number> {
  return [
    displayName(contact),
    contact.acn ? formatAcn(contact.acn) : "",
    contact.abn ? formatAbn(contact.abn) : "",
    FIRM_CATEGORY_META[contact.category].label,
    CONTACT_TYPE_META[contact.contactType].label,
    contact.email ?? "",
    contact.phone ?? "",
    firmContactState(contact) ?? "",
    formatAddress(contact),
    contact.jobsUsing,
    STATUS_LABEL[contact.status],
  ];
}

function buildCsv(
  headers: ReadonlyArray<string>,
  records: Array<Array<string | number>>,
): string {
  const lines: string[] = [headers.map(escapeCsvCell).join(",")];
  for (const record of records) {
    lines.push(record.map(escapeCsvCell).join(","));
  }
  return lines.join("\r\n");
}

function timestampSlug(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}` +
    `-${pad(d.getHours())}${pad(d.getMinutes())}`
  );
}

function downloadCsv(csvBody: string, filename: string): void {
  // Excel-friendly CSV: UTF-8 BOM so non-ASCII names render correctly when
  // double-clicked into Excel.
  const csv = "\uFEFF" + csvBody;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function scopeSlug(scope: FirmContactsCsvScope): string {
  switch (scope) {
    case "current":
      return "current-view";
    case "all":
      return "all";
    default: {
      const _exhaustive: never = scope;
      return _exhaustive;
    }
  }
}

/**
 * Dataset for the custom export tool (MOR-1048): the on-screen table layout
 * as a composable table the practitioner can filter, pivot, save and export
 * to Excel.
 */
export function buildFirmContactsCustomExportDatasets(
  rows: Array<FirmContactRow>,
): CustomExportDataset[] {
  return [
    {
      key: "firm-contacts",
      label: "Firm contacts",
      headers: [...TABLE_HEADERS],
      rows: rows.map((row) => rowToTableRecord(row)),
    },
  ];
}

/**
 * Download a table-shaped CSV that mirrors the on-screen firm contacts columns.
 */
export function exportFirmContactsTableToCsv(
  rows: Array<FirmContactRow>,
  scope: FirmContactsCsvScope,
): void {
  if (rows.length === 0) {
    throw new Error("No contacts to export.");
  }
  downloadCsv(
    buildCsv(
      TABLE_HEADERS,
      rows.map((row) => rowToTableRecord(row)),
    ),
    `firm-contacts-${scopeSlug(scope)}-${timestampSlug()}.csv`,
  );
}
